import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { 
  ChevronLeft, 
  ChevronRight, 
  Calendar as CalendarIcon, 
  Video, 
  User, 
  ExternalLink, 
  Clock,
  TrendingUp,
  BarChart3,
  Loader2,
  CalendarDays,
  FileCheck,
  MapPin,
  Briefcase
} from 'lucide-react';
import { 
  generateGoogleCalendarUrl, 
  downloadICSFile, 
  generateICSContent, 
  formatEventDateTime 
} from '../../utils/calendarUtils';

export default function AdminCalendar() {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState([]);
  const [profiles, setProfiles] = useState({});
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchAdminCalendarData();
  }, []);

  const fetchAdminCalendarData = async () => {
    setLoading(true);
    setError(null);
    try {
      const fetchedEvents = [];

      // 1. Fetch Profiles to map names
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('id, first_name, last_name');

      const profileMap = {};
      if (profileData) {
        profileData.forEach(p => {
          const fn = (p.first_name || '').trim();
          const ln = (p.last_name || '').trim();
          const name = (fn && fn !== 'null' && fn !== 'Unknown') ? `${fn} ${ln && ln !== 'null' ? ln : ''}`.trim() : '';
          profileMap[p.id] = name || p.email || 'Client';
        });
        setProfiles(profileMap);
      }

      // 2. Fetch all applications with interview status
      const { data: appData, error: appError } = await supabase
        .from('applications')
        .select('*')
        .eq('status', 'Interview');

      if (appError && appError.code !== '42P01') {
        throw appError;
      }

      if (appData) {
        appData.forEach(app => {
          if (app.interview_date) {
            fetchedEvents.push({
              id: `interview-${app.id}`,
              type: 'interview',
              title: `Interview: ${app.role} at ${app.company}`,
              clientName: profileMap[app.user_id] || 'Client Name Missing',
              company: app.company,
              role: app.role,
              date: app.interview_date, // YYYY-MM-DD
              time: app.interview_time || '10:00',
              location: app.interview_link || app.location || 'Video Call Link Provided',
              notes: app.interview_notes || 'Client strategy mock preparation.',
              userId: app.user_id
            });
          }
        });
      }

      // 3. Fetch all strategist coaching bookings
      const { data: bookingData, error: bookingError } = await supabase
        .from('bookings')
        .select('*')
        .neq('status', 'cancelled');

      if (bookingError && bookingError.code !== '42P01') {
        throw bookingError;
      }

      if (bookingData) {
        bookingData.forEach(book => {
          const bDate = book.scheduled_date || book.preferred_date;
          const bTime = book.scheduled_time || book.preferred_time;
          if (bDate) {
            fetchedEvents.push({
              id: `booking-${book.id}`,
              type: 'booking',
              title: `Coaching Session: ${book.topic || 'Strategy Session'}`,
              clientName: profileMap[book.user_id] || 'Client Name Missing',
              date: bDate, // YYYY-MM-DD
              time: bTime || '12:00',
              location: book.meeting_link || 'Google Meet Session',
              notes: book.admin_note || 'One-on-one strategy review.',
              userId: book.user_id
            });
          }
        });
      }

      setEvents(fetchedEvents);
    } catch (err) {
      console.error('Error fetching admin calendar items:', err);
      setError('Could not retrieve admin schedule.');
    } finally {
      setLoading(false);
    }
  };

  // Monthly navigation handlers
  const handlePrevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  // Calendar logic helpers
  const year = currentMonth.getFullYear();
  const month = currentMonth.getMonth();

  const firstDayOfMonth = new Date(year, month, 1);
  const startDayOfWeek = firstDayOfMonth.getDay();
  const totalDaysInMonth = new Date(year, month + 1, 0).getDate();
  const totalDaysInPrevMonth = new Date(year, month, 0).getDate();

  const calendarDays = [];

  // Add previous month filler days
  for (let i = startDayOfWeek - 1; i >= 0; i--) {
    calendarDays.push({
      date: new Date(year, month - 1, totalDaysInPrevMonth - i),
      isCurrentMonth: false,
    });
  }

  // Add current month days
  for (let i = 1; i <= totalDaysInMonth; i++) {
    calendarDays.push({
      date: new Date(year, month, i),
      isCurrentMonth: true,
    });
  }

  // Add next month filler days to complete grid
  const remainingCells = 42 - calendarDays.length;
  for (let i = 1; i <= remainingCells; i++) {
    calendarDays.push({
      date: new Date(year, month + 1, i),
      isCurrentMonth: false,
    });
  }

  // Check if dates are equivalent
  const isSameDay = (d1, d2) => {
    return d1.getFullYear() === d2.getFullYear() &&
           d1.getMonth() === d2.getMonth() &&
           d1.getDate() === d2.getDate();
  };

  // Format date to ISO key YYYY-MM-DD
  const getISODateString = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  // Get events on a specific day
  const getEventsForDay = (dateObj) => {
    const dateStr = getISODateString(dateObj);
    return events.filter(e => e.date === dateStr);
  };

  const selectedDateEvents = getEventsForDay(selectedDate);

  // Statistics calculation helpers
  const getStats = () => {
    const currentMonthKey = `${year}-${String(month + 1).padStart(2, '0')}`;
    const todayStr = getISODateString(new Date());

    const bookingsThisMonth = events.filter(e => e.type === 'booking' && e.date.startsWith(currentMonthKey)).length;
    const totalEventsThisMonth = events.filter(e => e.date.startsWith(currentMonthKey)).length;
    const activeTodayCount = events.filter(e => e.date === todayStr).length;

    return {
      totalEventsThisMonth,
      bookingsThisMonth,
      activeTodayCount
    };
  };

  const stats = getStats();

  if (loading && events.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[500px]">
        <Loader2 className="w-10 h-10 animate-spin text-indigo-950" />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8 space-y-8">
      {/* Admin Panel Header */}
      <div>
        <h1 className="text-3xl font-extrabold text-indigo-950 tracking-tight flex items-center gap-2">
          <span>Global Schedules & Bookings</span>
          <span className="text-xs font-semibold px-2.5 py-1 bg-amber-100 text-amber-800 rounded-full border border-amber-200">Admin Control</span>
        </h1>
        <p className="mt-2 text-sm text-slate-500">
          View, organize, and prepare for interviews and mentoring consultations across all enrolled clients.
        </p>
      </div>

      {/* Stats Cards Bar */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm flex items-center justify-between">
          <div className="space-y-1">
            <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">Total Events This Month</p>
            <p className="text-2xl font-black text-indigo-950">{stats.totalEventsThisMonth}</p>
          </div>
          <div className="p-3 bg-indigo-50 text-indigo-600 rounded-xl">
            <CalendarDays className="w-6 h-6" />
          </div>
        </div>

        <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm flex items-center justify-between">
          <div className="space-y-1">
            <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">Coaching Sessions This Month</p>
            <p className="text-2xl font-black text-indigo-950">{stats.bookingsThisMonth}</p>
          </div>
          <div className="p-3 bg-green-50 text-green-600 rounded-xl">
            <Video className="w-6 h-6" />
          </div>
        </div>

        <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm flex items-center justify-between">
          <div className="space-y-1">
            <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">Scheduled Today</p>
            <p className="text-2xl font-black text-indigo-950">{stats.activeTodayCount}</p>
          </div>
          <div className="p-3 bg-amber-50 text-amber-600 rounded-xl">
            <Clock className="w-6 h-6" />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Monthly Calendar View */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-2xl border border-slate-150 shadow-sm p-6 space-y-6">
            
            {/* Navigation Header */}
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold text-indigo-950">
                {currentMonth.toLocaleDateString('en-NG', { month: 'long', year: 'numeric' })}
              </h2>
              <div className="flex items-center gap-2">
                <button
                  onClick={handlePrevMonth}
                  className="p-2 border border-slate-200 rounded-lg text-slate-650 hover:bg-slate-50 transition-colors"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button
                  onClick={() => setCurrentMonth(new Date())}
                  className="px-3 py-2 border border-slate-200 rounded-lg text-xs font-bold text-slate-700 hover:bg-slate-50 transition-colors"
                >
                  Today
                </button>
                <button
                  onClick={handleNextMonth}
                  className="p-2 border border-slate-200 rounded-lg text-slate-650 hover:bg-slate-50 transition-colors"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Day labels */}
            <div className="grid grid-cols-7 text-center text-xs font-bold text-slate-400 border-b border-slate-100 pb-3">
              <div>Sun</div>
              <div>Mon</div>
              <div>Tue</div>
              <div>Wed</div>
              <div>Thu</div>
              <div>Fri</div>
              <div>Sat</div>
            </div>

            {/* Month grid */}
            <div className="grid grid-cols-7 gap-y-2 gap-x-2">
              {calendarDays.map((cell, index) => {
                const dayEvents = getEventsForDay(cell.date);
                const isSelected = isSameDay(cell.date, selectedDate);
                const isToday = isSameDay(cell.date, new Date());
                
                return (
                  <button
                    key={index}
                    onClick={() => setSelectedDate(cell.date)}
                    className={`
                      aspect-square rounded-xl p-1 flex flex-col justify-between items-center transition-all relative group
                      ${cell.isCurrentMonth ? 'text-slate-800 font-medium' : 'text-slate-300'}
                      ${isSelected ? 'bg-indigo-950 text-white font-bold shadow-md shadow-slate-100' : 'hover:bg-slate-50'}
                      ${isToday && !isSelected ? 'ring-2 ring-amber-500 ring-offset-2 font-bold' : ''}
                    `}
                  >
                    <span className="text-sm">{cell.date.getDate()}</span>
                    
                    {/* Events indicator badge */}
                    {dayEvents.length > 0 && (
                      <span className={`
                        text-[9px] font-black rounded-full px-1.5 py-0.5 mt-1
                        ${isSelected ? 'bg-white text-indigo-950' : 'bg-slate-100 text-slate-600 border border-slate-200'}
                      `}>
                        {dayEvents.length}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Day details */}
          <div className="space-y-4">
            <h3 className="text-lg font-extrabold text-indigo-950">
              Admin Log — {selectedDate.toLocaleDateString('en-NG', { weekday: 'long', month: 'short', day: 'numeric' })}
            </h3>

            {selectedDateEvents.length === 0 ? (
              <div className="bg-slate-50 border border-dashed border-slate-200 rounded-2xl p-8 text-center text-slate-500">
                <CalendarDays className="w-10 h-10 mx-auto text-slate-350 mb-2" />
                <p className="text-sm">No client bookings or interviews logged today.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {selectedDateEvents.map(evt => (
                  <div 
                    key={evt.id} 
                    className={`
                      p-5 rounded-2xl border bg-white shadow-sm flex flex-col sm:flex-row sm:items-center justify-between gap-4 transition-all hover:shadow-md
                      ${evt.type === 'interview' ? 'border-purple-100 hover:border-purple-200' : 'border-green-100 hover:border-green-200'}
                    `}
                  >
                    <div className="flex items-start gap-4">
                      <div className={`p-3 rounded-xl ${evt.type === 'interview' ? 'bg-purple-50 text-purple-600' : 'bg-green-50 text-green-600'}`}>
                        {evt.type === 'interview' ? <Briefcase className="w-6 h-6" /> : <Video className="w-6 h-6" />}
                      </div>
                      <div className="space-y-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${evt.type === 'interview' ? 'bg-purple-100 text-purple-800' : 'bg-green-100 text-green-800'}`}>
                            {evt.type === 'interview' ? 'Interview' : 'Coaching Session'}
                          </span>
                          <span className="text-xs font-bold text-slate-700 bg-slate-100 px-2 py-0.5 rounded-full flex items-center gap-1">
                            <User className="w-3.5 h-3.5" /> {evt.clientName}
                          </span>
                        </div>
                        <h4 className="font-bold text-slate-900 text-base mt-1">{evt.title}</h4>
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-slate-500">
                          <span className="flex items-center gap-1"><Clock className="w-4 h-4" /> {formatEventDateTime(evt.date, evt.time)}</span>
                          <span className="flex items-center gap-1">
                            {evt.location.startsWith('http') ? (
                              <a href={evt.location} target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline inline-flex items-center gap-0.5 font-semibold">
                                Join Call <ExternalLink className="w-3.5 h-3.5" />
                              </a>
                            ) : (
                              <><MapPin className="w-4 h-4" /> {evt.location}</>
                            )}
                          </span>
                        </div>
                        {evt.notes && <p className="text-xs text-slate-400 mt-2 italic max-w-xl">"{evt.notes}"</p>}
                      </div>
                    </div>

                    {/* Google Calendar save actions for administrator */}
                    <div className="flex items-center gap-2">
                      <a
                        href={generateGoogleCalendarUrl({
                          title: `[Admin] ${evt.title}`,
                          date: evt.date,
                          time: evt.time,
                          description: `Client: ${evt.clientName}\nNotes: ${evt.notes}`,
                          location: evt.location
                        })}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-center gap-1.5 px-3 py-2 border border-slate-200 rounded-xl text-xs font-bold text-slate-700 bg-white hover:bg-slate-50 hover:border-slate-350 transition-colors shadow-sm"
                      >
                        <Clock className="w-4 h-4 text-indigo-600" />
                        <span>Add to Google Calendar</span>
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Sidebar details */}
        <div className="space-y-6">
          <div className="bg-indigo-950 rounded-2xl text-white p-6 shadow-sm space-y-4">
            <h3 className="text-base font-bold text-amber-400 flex items-center gap-2">
              <TrendingUp className="w-5 h-5" />
              <span>Mentoring Checklist</span>
            </h3>
            <p className="text-xs text-slate-300 leading-relaxed">
              When scheduling interviews or strategy slots, ensure details like meeting coordinates are clearly defined.
            </p>
            <ul className="space-y-2 text-xs text-slate-200 border-t border-white/10 pt-4">
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-amber-400 rounded-full mt-1.5" />
                <span>Video links should include protocol prefix (`https://`).</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-amber-400 rounded-full mt-1.5" />
                <span>Always fill out descriptions to help candidates prepare.</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
